import type { DeclencheursComposes } from '../shell/menu-contextuel-outils.js'
import type { VisibilityMode } from './registry.js'

/**
 * MENU CLIC DROIT — TUILES ET CARTES (lot 2, 24/10/2026) : ce que le menu
 * propose, calculé sans React ni navigateur (tests/menu-tuiles.test.ts).
 * `menu-tuiles.tsx` n'y ajoute que les icônes, la copie et la lecture du
 * document (sélection, largeur d'écran).
 *
 * Chaque entrée reprend un geste qui existe déjà sur la page :
 *
 *   · tuile de cockpit (LayoutGrid)   « Modifier la page », le bouton réglages
 *     (la MÊME fenêtre), son choix de visibilité, « Disposition par défaut » :
 *     un brouillon, que la barre d'enregistrement confirme (PUT
 *     /api/v1/page-layout/:page, soiree.manage) — pour qui a `can_edit` seulement ;
 *   · section de la page Soirées      les flèches ↑/↓ de la poignée, en édition ;
 *   · carte de l'accueil, tuile de hub, service du catalogue : les liens
 *     qu'elles portent déjà, et rien d'autre.
 *
 * Ni déplacer pas à pas, ni redimensionner, ni masquer une carte de
 * l'accueil : ces gestes n'existent nulle part, le menu ne les invente pas.
 */

/* ─── les gestes qui appartiennent à autre chose que le menu ─── */

/** Les poignées d'une tuile en édition : un appui y lance un glisser. */
export const POIGNEES_TUILE = '.lg-widget__grip, .lg-widget__resize'
/** La poignée d'une section de la page Soirées. */
export const POIGNEE_SECTION = '.so-reorder__grip'
/** Un champ de saisie garde le menu du navigateur : Coller, Annuler la frappe. */
export const CHAMPS_NATIFS = 'input, textarea, select, [contenteditable]:not([contenteditable="false"])'

/** Un autre menu, posé sur un élément DANS la tuile (une personne du Personnel…). */
export const MENU_IMBRIQUE = '[data-menu-contextuel]'
/** Un lien DANS la tuile : il garde le menu du navigateur (ouvrir dans un onglet…). */
export const LIEN_IMBRIQUE = 'a[href]'

/** Le geste qui ouvrirait le menu : clic droit, appui (long, au doigt), clavier. */
export type Geste = 'clic-droit' | 'appui' | 'clavier'
/** Ce qu'une exclusion lit de l'événement React : rien que React n'y mette déjà. */
export interface EvenementGeste {
  readonly target: unknown
  readonly currentTarget: unknown
  readonly button?: unknown
  readonly clientX?: unknown
  readonly clientY?: unknown
  readonly pointerType?: unknown
  readonly nativeEvent?: unknown
}
/** Vrai quand ce geste, à cet endroit, revient à la page ou au navigateur. */
export type Exclusion = (e: EvenementGeste, geste: Geste) => boolean

/** La cible (ou un de ses parents) répond-elle au sélecteur ? Faux hors du DOM. */
export function cibleDans(cible: unknown, selecteur: string): boolean {
  const t = cible as { closest?: (s: string) => unknown } | null
  return typeof t?.closest === 'function' && !!t.closest(selecteur)
}

/**
 * Les déclencheurs d'un menu, sauf là où le geste appartient à autre chose.
 *
 * Une tuile en édition porte deux poignées : un appui long y lance À LA FOIS
 * le glisser et le minuteur du menu (LayoutGrid.tsx, SectionOrder.tsx). Le
 * menu s'efface donc sur elles : au clic droit, le menu du navigateur reste ;
 * à l'appui, le minuteur est aussitôt annulé — comme `composerDeclencheurs`
 * quand la page prend l'appui. Le clavier n'y lance aucun glisser : la touche
 * Menu y reste le seul chemin vers le menu de la tuile.
 *
 * Menu inerte (`{}`) : rendu tel quel.
 */
export function sauf(declencheurs: DeclencheursComposes, exclu: Exclusion): DeclencheursComposes {
  const { onContextMenu, onKeyDown, onPointerDown, onPointerCancel } = declencheurs
  const r: DeclencheursComposes = { ...declencheurs }
  if (onContextMenu) r.onContextMenu = (e) => { if (!exclu(e, 'clic-droit')) onContextMenu(e) }
  if (onKeyDown) r.onKeyDown = (e) => { if (!exclu(e, 'clavier')) onKeyDown(e) }
  if (onPointerDown) {
    r.onPointerDown = (e) => {
      // Toujours appelé : il remet à zéro la garde du clic d'après l'appui long.
      onPointerDown(e)
      if (exclu(e, 'appui')) onPointerCancel?.(e)
    }
  }
  return r
}

/* ─── là où une tuile pleine de contenu rend le menu au navigateur ─── */

type Contenant = { contains?: (n: unknown) => boolean } | null

/** `parent` contient-il `n` dans le DOM ? Faux hors du DOM, jamais d'exception. */
function contient(parent: unknown, n: unknown): boolean {
  const p = parent as Contenant
  if (typeof p?.contains !== 'function' || typeof n !== 'object' || n === null) return false
  try { return p.contains(n) } catch { return false }
}

/**
 * L'événement vient-il d'AILLEURS que l'élément qui porte le menu ?
 *
 * React fait remonter un événement né dans un portail le long de l'arbre
 * React, pas du DOM : un clic droit dans le tiroir « Détail des sources » ou
 * dans la confirmation « Marquer comme annoncée », que la tuile monte en
 * portail dans `body`, arrive jusqu'à la <section> de la tuile. Ce n'est pas
 * la tuile : son menu ne s'ouvre pas par-dessus une fenêtre qui n'a rien à
 * voir avec lui, et le navigateur (ou la fenêtre) garde le geste.
 */
export function horsHote(e: EvenementGeste): boolean {
  return !contient(e.currentTarget, e.target)
}

/**
 * La cible est-elle dans un élément `selecteur` imbriqué DANS l'hôte — et non
 * l'hôte lui-même, ni un parent de l'hôte ?
 */
export function imbrique(e: EvenementGeste, selecteur: string): boolean {
  const t = e.target as { closest?: (s: string) => unknown } | null
  const trouve = typeof t?.closest === 'function' ? t.closest(selecteur) : null
  return !!trouve && trouve !== e.currentTarget && contient(e.currentTarget, trouve)
}

/** Le doigt : l'appui long y est le geste du menu, pas celui d'une sélection. */
export function auDoigt(e: EvenementGeste): boolean {
  const natif = e.nativeEvent as { pointerType?: unknown } | null | undefined
  return e.pointerType === 'touch' || natif?.pointerType === 'touch'
}

/**
 * La sélection lue à l'appui du bouton droit, AVANT que le navigateur la
 * change : sur macOS, Chrome et Safari sélectionnent le mot sous le pointeur
 * juste avant d'envoyer `contextmenu`. Relue au clic droit, elle n'était
 * jamais vide sur du texte, et le menu ne s'ouvrait que sur les marges.
 */
export interface AvantAppui { x: number; y: number; t: number; surSelection: boolean }

/**
 * Ce clic droit vient-il de cet appui ? Même point (à 2 px près : l'appui
 * donne des coordonnées fractionnaires), moins de 2 s après. Sinon (bouton
 * tenu longtemps sous Windows, souris déplacée), la sélection n'a pas changé
 * entre-temps : on la relit.
 */
export function memeAppui(a: AvantAppui | null, x: number, y: number, t: number): a is AvantAppui {
  return a !== null && Math.abs(a.x - x) <= 2 && Math.abs(a.y - y) <= 2 && t - a.t >= 0 && t - a.t < 2000
}

/** Ce que l'exclusion demande au document. */
export interface LectureSelection {
  /** Appui d'un pointeur dans l'hôte : retenir la sélection sous le point, avant le navigateur. */
  retenir: (e: EvenementGeste) => void
  /** Clic droit à la souris : le point était-il sur un texte déjà sélectionné ? */
  surSelection: (e: EvenementGeste) => boolean
}

const nombre = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null)

/**
 * La lecture de la sélection pour toute une page : retenue à l'appui du
 * bouton droit, relue seulement quand le clic droit ne vient pas de cet
 * appui. Une seule mémoire : elle décrit le document, pas une tuile, et deux
 * hôtes imbriqués (section de la page Soirées, tuile) lisent le même appui.
 *
 * `pointSurSelection` lit le document (menu-tuiles.tsx) ; `maintenant`, l'heure.
 */
export function lectureSelection(
  pointSurSelection: (x: number, y: number) => boolean,
  maintenant: () => number = Date.now,
): LectureSelection {
  let avant: AvantAppui | null = null
  return {
    retenir: (e) => {
      const x = nombre(e.clientX), y = nombre(e.clientY)
      avant = !auDoigt(e) && e.button === 2 && x !== null && y !== null
        ? { x, y, t: maintenant(), surSelection: pointSurSelection(x, y) }
        : null
    },
    surSelection: (e) => {
      const x = nombre(e.clientX), y = nombre(e.clientY)
      if (x === null || y === null) return false
      const a = avant
      return memeAppui(a, x, y, maintenant()) ? a.surSelection : pointSurSelection(x, y)
    },
  }
}

/**
 * Où une tuile pleine de contenu garde le menu du navigateur, dans cet ordre :
 *
 *   1. ce qui vient d'un portail (tiroir, confirmation, liste d'un Select) :
 *      tous les gestes, clavier compris — Maj+F10 sur « C'est annoncé » ;
 *   2. un champ de saisie (Coller, Annuler la frappe) : tous les gestes ;
 *   3. le clavier ouvre le menu partout ailleurs ;
 *   4. les poignées de glisser (`poignees`) : jamais le menu au pointeur ;
 *   5. l'appui sur un menu imbriqué ou un lien imbriqué : le minuteur de la
 *      tuile est annulé aussitôt armé. Seul celui du menu imbriqué reste —
 *      sinon, armé en dernier, il l'emportait (le défaut que `garderAppui`
 *      corrige dans menu-salle.tsx) ; un lien n'en a pas ;
 *   6. le clic droit à la souris sur un texte déjà sélectionné : le copier.
 *
 * Le lien imbriqué au clic droit, et Maj + clic droit, restent au socle
 * (useMenuContextuel).
 */
export function exclusionNative(poignees: string | null, lecture: LectureSelection): Exclusion {
  return (e, geste) => {
    if (horsHote(e)) return true
    if (cibleDans(e.target, CHAMPS_NATIFS)) return true
    if (geste === 'clavier') return false
    if (geste === 'appui') lecture.retenir(e)
    if (poignees !== null && cibleDans(e.target, poignees)) return true
    if (geste === 'appui') return imbrique(e, MENU_IMBRIQUE) || imbrique(e, LIEN_IMBRIQUE)
    return !auDoigt(e) && lecture.surSelection(e)
  }
}

/* ─── tuile de cockpit (LayoutGrid) ─── */

/** Les choix de visibilité, dans l'ordre de la fenêtre « Configurer la tuile » (MODES). */
export const ORDRE_VISIBILITE: readonly VisibilityMode[] = ['ALWAYS', 'BEFORE_START', 'FROM_START', 'HIDDEN']

/**
 * La largeur sous laquelle la barre d'une tuile (poignée, réglages) n'existe
 * pas (layout.css) : l'édition est desktop. Le menu n'y propose donc ni
 * « Configurer » ni « Visibilité », un geste que l'écran n'offre pas.
 */
export const ECRAN_ETROIT = '(max-width: 900px)'

/* ─── section de la page Soirées (SectionOrder) ─── */

/** La place d'une section, et si elle peut encore monter ou descendre d'un cran. */
export function crans(ordre: readonly string[], id: string): { index: number; monter: boolean; descendre: boolean } {
  const index = ordre.indexOf(id)
  return { index, monter: index > 0, descendre: index >= 0 && index < ordre.length - 1 }
}

/* ─── liens ─── */

/** Un chemin de Gestion (le routeur ne sait suivre que ceux-là). */
export function estCheminInterne(href: string | null | undefined): href is string {
  return typeof href === 'string' && href.startsWith('/') && !href.startsWith('//')
}

/**
 * Le lien d'un service externe, ouvert dans un onglet : http(s) seulement,
 * et tel que le bouton de la carte l'écrit (même adresse, pas une réécriture).
 */
export function lienServiceSur(url: string | null | undefined): string | null {
  if (typeof url !== 'string') return null
  try {
    const p = new URL(url).protocol
    return p === 'https:' || p === 'http:' ? url : null
  } catch {
    return null
  }
}

/** La fiche d'un service du catalogue, comme HeroCard et StandardRow l'écrivent (InfraCatalog.tsx). */
export const cheminServiceInfra = (slug: string): string => `/infrastructure/services/${slug}`

/* ─── carte de l'accueil « / » (GlobalDashboard) ─── */

export type CarteAccueil =
  | { sorte: 'service'; url: string | null }
  | { sorte: 'rh' }
  | { sorte: 'infrastructure' }
  | { sorte: 'communication' }
  /** `famille` : la catégorie (SECURITY, PARC, COMMUNICATION…). */
  | { sorte: 'non-configuree'; famille: string }
  /** `soireeId` : la soirée en cours ; `null` = carte calme. */
  | { sorte: 'ce-soir'; soireeId: string | null; interdit: boolean }

export interface LiensCarte {
  /** Le lien interne de la carte (son bouton), avec le libellé du bouton. */
  ouvrir: { href: string; libelle: string } | null
  /** Le bouton externe d'une carte de service, ouvert dans un nouvel onglet. */
  service: string | null
  /** Apparence › Tableau de bord : la seule page qui masque une carte de l'accueil. */
  apparence: boolean
}

export const CHEMIN_APPARENCE = '/settings/appearance'

/**
 * Les liens d'une carte de l'accueil, ceux de ses boutons (GlobalDashboard.tsx)
 * et rien d'autre. Apparence n'est proposée qu'aux deux cartes qu'une
 * préférence y masque : « Ce soir » (dashboard_show_tonight) et une famille
 * non configurée (dashboard_show_unconfigured) — sauf celle de Communication,
 * que l'accueil montre quelle que soit la préférence : y envoyer quelqu'un
 * pour la masquer serait lui promettre un réglage qui ne fait rien.
 */
export function liensCarteAccueil(c: CarteAccueil): LiensCarte {
  switch (c.sorte) {
    case 'service': return { ouvrir: null, service: lienServiceSur(c.url), apparence: false }
    case 'rh': return { ouvrir: { href: '/equipe/rh', libelle: 'Ouvrir RH' }, service: null, apparence: false }
    case 'infrastructure': return { ouvrir: { href: '/infrastructure', libelle: 'Ouvrir l’infrastructure' }, service: null, apparence: false }
    case 'communication': return { ouvrir: { href: '/communication', libelle: 'Ouvrir la communication' }, service: null, apparence: false }
    case 'non-configuree': return { ouvrir: null, service: null, apparence: c.famille !== 'COMMUNICATION' }
    case 'ce-soir': return {
      ouvrir: c.soireeId !== null
        ? { href: `/soirees/${c.soireeId}`, libelle: 'Ouvrir la soirée' }
        : c.interdit ? null : { href: '/soirees', libelle: 'Ouvrir les soirées' },
      service: null,
      apparence: true,
    }
  }
}
